// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// VYLAPP — FeedPage + supporting pages
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
import { useState, useEffect, useRef } from "react";
import { useFeedStore, useAuthStore, useUIStore } from "../context/store.js";
import { Avatar, LiveBadge, VybePill, Ic, ICONS, TranslateToggle } from "../components/UI.jsx";
import { formatDistanceToNow } from "date-fns";
import { getSocket } from "../hooks/useSocket.js";

const B = {
  bg: "#08070F", bg2: "#0D0C18", bg3: "#131124",
  violet: "#7C3AED", violetLt: "#8B5CF6",
  violetDim: "rgba(124,58,237,0.15)", violetBorder: "rgba(124,58,237,0.3)",
  vibeGreen: "#10F5A0", vibeGreenDim: "rgba(16,245,160,0.12)",
  coral: "#FF6B6B", coralDim: "rgba(255,107,107,0.12)", coralBorder: "rgba(255,107,107,0.28)",
  amber: "#FFB830", sky: "#38BDF8", skyDim: "rgba(56,189,248,0.12)",
  purple: "#A78BFA", purpleDim: "rgba(167,139,250,0.12)",
  text: "#F0EFFE", text2: "#7C7A9E", text3: "#3D3B5C",
  border: "rgba(124,58,237,0.15)", border2: "rgba(124,58,237,0.08)",
  grad: "linear-gradient(135deg, #7C3AED, #10F5A0)",
  gradCoral: "linear-gradient(135deg, #7C3AED, #FF6B6B)",
  font: "'Sora', system-ui, sans-serif",
  mono: "'Space Mono', monospace",
};

const numFmt = n => !n ? "0" : n >= 1000 ? `${(n/1000).toFixed(1)}k` : String(n);
const timeAgo = d => { try { return formatDistanceToNow(new Date(d), { addSuffix: true }); } catch { return "just now"; } };

// ── Compose box ───────────────────────────────────────────────────────────────
function ComposeBox({ onStream }) {
  const { user }    = useAuthStore();
  const { postVibe } = useFeedStore();
  const showToast   = useUIStore(s => s.showToast);
  const [text, setText] = useState("");
  const [cat,  setCat]  = useState("General");
  const [posting, setPosting] = useState(false);

  const cats = ["General","Tech Vibes","Global Connect","Creative Learn","Human Potential","AgriTech","DAO Governance"];

  const submit = async () => {
    if (!text.trim()) return;
    setPosting(true);
    try {
      const vibe = await postVibe({ content: text.trim(), category: cat });
      const socket = getSocket();
      socket?.emit("vibe:new", vibe);
      setText("");
      showToast("Vibe posted! 🔥", "success");
    } catch { showToast("Failed to post vibe", "error"); }
    setPosting(false);
  };

  return (
    <div style={{ display:"flex", gap:12, padding:"14px 16px", borderBottom:`1px solid ${B.border2}` }}>
      {user && <Avatar user={user} size={42} ring />}
      <div style={{ flex:1 }}>
        <textarea value={text} onChange={e => setText(e.target.value)} onKeyDown={e => e.key==="Enter" && e.metaKey && submit()}
          placeholder="What's your vibe today? Share with the world…"
          rows={2} style={{ background:"none", border:"none", color:B.text, fontFamily:B.font, fontSize:18, outline:"none", width:"100%", resize:"none", minHeight:56, lineHeight:1.5 }} />
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", borderTop:`1px solid ${B.border2}`, paddingTop:10, marginTop:4 }}>
          <div style={{ display:"flex", gap:8, alignItems:"center" }}>
            <select value={cat} onChange={e => setCat(e.target.value)} style={{ background:B.bg3, border:`1px solid ${B.border2}`, borderRadius:8, padding:"4px 8px", color:B.text2, fontFamily:B.mono, fontSize:11, outline:"none" }}>
              {cats.map(c => <option key={c} value={c} style={{ background:B.bg3 }}>{c}</option>)}
            </select>
            <button onClick={onStream} style={{ display:"flex", alignItems:"center", gap:4, padding:"4px 10px", borderRadius:8, border:"none", background:B.coralDim, color:B.coral, fontFamily:B.font, fontSize:12, cursor:"pointer" }}>
              <Ic d={ICONS.stream} s={12} c={B.coral} /> Live
            </button>
          </div>
          <div style={{ display:"flex", alignItems:"center", gap:12 }}>
            {text.length > 0 && <span style={{ fontSize:13, color: text.length > 260 ? B.coral : B.text2 }}>{280 - text.length}</span>}
            <button onClick={submit} disabled={!text.trim() || posting} style={{
              padding:"8px 22px", borderRadius:9999, border:"none",
              background: text.trim() && !posting ? B.grad : `${B.violet}40`,
              color:"#fff", fontFamily:B.font, fontWeight:700, fontSize:15,
              cursor: text.trim() && !posting ? "pointer" : "default",
              boxShadow: text.trim() && !posting ? "0 4px 20px rgba(124,58,237,0.4)" : "none",
              transition:"all 0.2s",
              display:"flex", alignItems:"center", gap:6,
            }}>
              {posting ? <><div className="vyl-spinner" /> Posting…</> : "✦ Vibe"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Single Vibe card ──────────────────────────────────────────────────────────
function VibeCard({ post, onCall }) {
  const { user }      = useAuthStore();
  const { likeVibe, repostVibe } = useFeedStore();
  const showToast     = useUIStore(s => s.showToast);
  const [localLiked,  setLocalLiked]  = useState(post.likes?.includes(user?._id));
  const [localLikes,  setLocalLikes]  = useState(post.likes?.length || 0);
  const [localReposted, setLocalReposted] = useState(false);
  const [localReposts,  setLocalReposts]  = useState(post.reposts?.length || 0);

  const catColor = { "Tech Vibes":B.sky,"Global Connect":B.vibeGreen,"Creative Learn":B.amber,"Human Potential":B.purple,"AgriTech":B.vibeGreen,"DAO Governance":B.coral,"General":B.violet };
  const cc = catColor[post.category] || B.violet;

  const handleLike = async () => {
    setLocalLiked(v => !v);
    setLocalLikes(v => localLiked ? v-1 : v+1);
    try { await likeVibe(post._id); } catch {}
  };

  const handleRepost = async () => {
    setLocalReposted(v => !v);
    setLocalReposts(v => localReposted ? v-1 : v+1);
    try { await repostVibe(post._id); showToast("Vibed! 🔁","success"); } catch {}
  };

  const actions = [
    { icon: ICONS.comment, count: post.replies?.length, color: B.sky,        hoverBg: B.skyDim   },
    { icon: ICONS.repeat,  count: localReposts,         color: B.vibeGreen,  hoverBg: B.vibeGreenDim, active: localReposted, toggle: handleRepost },
    { icon: ICONS.heart,   count: localLikes,           color: B.coral,      hoverBg: B.coralDim,     active: localLiked,    toggle: handleLike, fill: localLiked },
    { icon: ICONS.share,   count: null,                 color: B.violetLt,   hoverBg: B.violetDim },
  ];

  return (
    <div style={{ display:"flex", gap:12, padding:"14px 16px", borderBottom:`1px solid ${B.border2}`, cursor:"pointer", transition:"background 0.15s" }}
      onMouseEnter={e => e.currentTarget.style.background="rgba(124,58,237,0.04)"}
      onMouseLeave={e => e.currentTarget.style.background="none"}>
      <Avatar user={post.author} size={42} showOnline />
      <div style={{ flex:1, minWidth:0 }}>
        {post.category && post.category !== "General" && <div style={{ marginBottom:6 }}><VybePill label={post.category} color={cc} /></div>}
        <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:2 }}>
          <span style={{ fontWeight:700, fontSize:15, color:B.text }}>{post.author?.displayName || "Viber"}</span>
          {post.author?.verified && <span style={{ color:B.violet, fontSize:13 }}>✓</span>}
          <span style={{ fontSize:14, color:B.text2 }}>{post.author?.handle}</span>
          <span style={{ color:B.text3 }}>·</span>
          <span style={{ fontSize:13, color:B.text2 }}>{timeAgo(post.createdAt)}</span>
          <button onClick={e => e.stopPropagation()} style={{ marginLeft:"auto", background:"none", border:"none", cursor:"pointer" }}>
            <Ic d={ICONS.dots} s={18} c={B.text2} sw={2.5} />
          </button>
        </div>

        <div style={{ fontSize:15, lineHeight:1.65, color:B.text, marginBottom:10, whiteSpace:"pre-wrap" }}>
          {post.content?.split(/(\s+)/).map((w,i) =>
            w.startsWith("#") ? <span key={i} style={{ color:B.violetLt }}>{w}</span>
            : w.startsWith("@") ? <span key={i} style={{ color:B.vibeGreen }}>{w}</span>
            : w
          )}
        </div>

        {/* Event card */}
        {post.isEvent && post.event?.title && (
          <div style={{ borderRadius:14, overflow:"hidden", border:`1px solid ${B.border}`, marginBottom:10 }}>
            <div style={{ padding:"12px 14px", background:B.bg3 }}>
              <div style={{ fontWeight:700, fontSize:15 }}>{post.event.title}</div>
              {post.event.subtitle && <div style={{ fontSize:13, color:B.text2, marginTop:2 }}>{post.event.subtitle}</div>}
            </div>
            <div style={{ display:"flex", borderTop:`1px solid ${B.border2}` }}>
              {[{v:post.event.reminded,l:"REMINDED"},{v:post.event.interested,l:"INTERESTED"},{v:post.event.time,l:"TIME"}].map((s,i) => (
                <div key={i} style={{ flex:1, padding:"10px 8px", textAlign:"center", borderRight:i<2?`1px solid ${B.border2}`:"none" }}>
                  <div style={{ fontWeight:900, fontSize:18, background:B.grad, WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent" }}>{s.v||"—"}</div>
                  <div style={{ fontSize:10, color:B.text2, letterSpacing:0.8, marginTop:2, fontFamily:B.mono }}>{s.l}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Actions */}
        <div style={{ display:"flex", alignItems:"center", marginTop:4 }}>
          {actions.map((a,i) => (
            <button key={i} onClick={e => { e.stopPropagation(); a.toggle?.(); }}
              style={{ display:"flex", alignItems:"center", gap:6, padding:"7px 10px", borderRadius:9999, border:"none", background:"none", color:a.active?a.color:B.text2, cursor:"pointer", fontSize:13, fontFamily:B.font, transition:"all 0.15s", minWidth:50 }}
              onMouseEnter={e => { e.currentTarget.style.background=a.hoverBg; e.currentTarget.style.color=a.color; }}
              onMouseLeave={e => { e.currentTarget.style.background="none"; e.currentTarget.style.color=a.active?a.color:B.text2; }}>
              <Ic d={a.icon} s={17} c={a.active?a.color:B.text2} f={a.fill?a.color:"none"} />
              {a.count!=null ? numFmt(a.count) : ""}
            </button>
          ))}
          {post.globalImpact && <span style={{ marginLeft:"auto", padding:"2px 8px", borderRadius:6, fontSize:11, fontWeight:700, color:B.vibeGreen, background:B.vibeGreenDim, fontFamily:B.mono }}>🌍 GLOBAL IMPACT</span>}
          <div style={{ display:"flex", gap:4, marginLeft:post.globalImpact?"8px":"auto" }}>
            <button onClick={e => { e.stopPropagation(); onCall({ type:"voice", user:post.author }); }}
              style={{ width:28, height:28, borderRadius:"50%", border:"none", background:B.vibeGreenDim, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center" }}>
              <Ic d={ICONS.phone} s={12} c={B.vibeGreen} />
            </button>
            <button onClick={e => { e.stopPropagation(); onCall({ type:"video", user:post.author }); }}
              style={{ width:28, height:28, borderRadius:"50%", border:"none", background:B.skyDim, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center" }}>
              <Ic d={ICONS.video} s={12} c={B.sky} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── FeedPage ──────────────────────────────────────────────────────────────────
export default function FeedPage({ onCall, onStream }) {
  const { vibes, fetchVibes, loading, hasMore, setTab, tab } = useFeedStore();
  const scrollRef = useRef(null);
  const [activeTab, setActiveTab] = useState("for-you");

  useEffect(() => { fetchVibes(true); }, [activeTab]);

  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el || loading || !hasMore) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 200) fetchVibes(false);
  };

  const switchTab = (t) => { setActiveTab(t); setTab(t); };

  return (
    <div style={{ display:"flex", flexDirection:"column", height:"100%", overflow:"hidden" }}>
      {/* Tab bar */}
      <div style={{ display:"flex", borderBottom:`1px solid ${B.border2}`, flexShrink:0 }}>
        {[["for-you","For You"],["following","Following"]].map(([k,l]) => (
          <button key={k} onClick={() => switchTab(k)} style={{
            flex:1, padding:"15px 0", textAlign:"center", fontSize:15,
            fontWeight:activeTab===k?700:400, color:activeTab===k?B.text:B.text2,
            border:"none", background:"none", cursor:"pointer", fontFamily:B.font,
            borderBottom:activeTab===k?`3px solid ${B.violet}`:"3px solid transparent",
          }}
            onMouseEnter={e => e.currentTarget.style.background=B.violetDim}
            onMouseLeave={e => e.currentTarget.style.background="none"}>{l}</button>
        ))}
        <button onClick={onStream} style={{ flex:1, padding:"15px 0", display:"flex", alignItems:"center", justifyContent:"center", gap:6, fontSize:15, color:B.coral, border:"none", background:"none", cursor:"pointer", fontFamily:B.font, borderBottom:"3px solid transparent" }}
          onMouseEnter={e => e.currentTarget.style.background=B.coralDim}
          onMouseLeave={e => e.currentTarget.style.background="none"}>
          <Ic d={ICONS.stream} s={14} c={B.coral} /> Go Live
        </button>
      </div>

      {/* Scrollable feed */}
      <div ref={scrollRef} onScroll={handleScroll} style={{ flex:1, overflowY:"auto" }}>
        <ComposeBox onStream={onStream} />
        {vibes.map(vibe => <VibeCard key={vibe._id} post={vibe} onCall={onCall} />)}
        {loading && <div style={{ padding:24, textAlign:"center" }}><div className="vyl-spinner" style={{ margin:"0 auto" }} /></div>}
        {!hasMore && vibes.length > 0 && <div style={{ padding:24, textAlign:"center", color:B.text2, fontSize:14 }}>You've seen all the vibes ✨</div>}
        {!loading && vibes.length === 0 && (
          <div style={{ padding:48, textAlign:"center" }}>
            <div style={{ fontSize:48, marginBottom:16 }}>⚡</div>
            <div style={{ fontWeight:700, fontSize:20, color:B.text, marginBottom:8 }}>No vibes yet</div>
            <div style={{ color:B.text2 }}>Be the first to vibe!</div>
          </div>
        )}
      </div>
    </div>
  );
}
